/**
 * InvoiceComponent - Displays service invoice with proper tax and discount calculations
 *
 * Modes:
 * - 'preview': Frontend calculation for service selection/checkout (before payment)
 * - 'confirmed': Uses backend calculated values from completed service orders
 * - 'error': Frontend calculation when backend data is unavailable (recovery mode)
 *
 * Props:
 * - mode: 'preview' | 'confirmed' | 'error' (default: 'preview')
 * - serviceOrder: Backend service order data (required for 'confirmed' mode)
 * - onError: Callback for error handling (optional)
 *
 * The component will automatically detect data inconsistencies and call onError
 * when backend data is missing or invalid in 'confirmed' mode.
 */

import React, { useMemo, useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import QText from './QText';
import Button from './Button';
import { goBackHandler, formatServicePrice } from '../utils';
import BackArrowIcon from './Icons/BackArrowIcon';

const InvoiceComponent = ({
  selectedServices = [],
  queueDetail,
  onNext,
  onBack,
  onPayNow,
  onSettleOffline,
  showQueueInfo = true,
  hideBackButton = false,
  fullWidth = false,
  showFooterButtons = true,
  paymentStatus = 'pending',
  paymentId = null,
  serviceOrderId = null,
  serviceOrder = null, // New prop for backend service order data
  mode = 'preview', // New prop: 'preview' | 'confirmed' | 'error'
  isAddUserFlow = false,
  hidePaymentStatus = false,
  navigation,
  showPaymentOptions = false, // New prop to show payment method selection
  isUserFlow = false, // New prop to indicate if this is user flow (only online payment)
  onSkipPayment = null, // Callback for skip payment
  onOnlinePayment = null, // Callback for online payment
  onCashPayment = null, // Callback for cash payment
  excludeJoiningFee = false, // New prop to exclude joining fees (for buy more services)
  onError = null, // Callback for error handling
}) => {
  const theme = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { t } = useTranslation();
  const hookNavigation = useNavigation();

  // Helper function to format service price - using centralized utility
  const formatPrice = (price, currency = 'INR') => {
    return formatServicePrice(price, {
      currency,
      t,
      useCurrencyCode: true,
    });
  };

  const nav = navigation || hookNavigation;

  const generateInvoiceNumber = () => {
    const prefix = 'ISK';
    const timestamp = Date.now().toString().slice(-7);
    return `${prefix}${timestamp}`;
  };

  const calculateTotals = () => {
    try {
      // Use backend calculated values when in confirmed mode with service order data
      if (mode === 'confirmed' && serviceOrder && selectedServices.length > 0) {
        // Validate that we have proper backend calculated values
        const hasValidBackendData =
          selectedServices[0].total_without_tax !== undefined;

        if (hasValidBackendData) {
          const backendSubtotal = selectedServices.reduce((sum, service) => {
            return sum + parseFloat(service.total_without_tax || 0);
          }, 0);

          const backendTotalTax = selectedServices.reduce((sum, service) => {
            const sgst = parseFloat(service.total_sgst || 0);
            const cgst = parseFloat(service.total_cgst || 0);
            return sum + sgst + cgst;
          }, 0);

          const backendTotal = selectedServices.reduce((sum, service) => {
            return sum + parseFloat(service.total_with_tax || 0);
          }, 0);

          const queueJoiningFee =
            !excludeJoiningFee &&
            queueDetail?.price_type === 'Paid' &&
            queueDetail?.price
              ? queueDetail.price
              : 0;

          return {
            servicesSubtotal: backendSubtotal,
            servicesPreTaxTotal: backendSubtotal,
            servicesTotalTax: backendTotalTax,
            queueJoiningFee,
            subtotal: backendSubtotal + queueJoiningFee,
            totalTax: backendTotalTax,
            total: backendTotal + queueJoiningFee,
            hasGST: backendTotalTax > 0,
          };
        } else {
          // Missing backend data in confirmed mode - this is an error
          const error = new Error(
            'Backend service order data is incomplete or invalid'
          );
          if (onError) {
            onError(error);
          } else {
            console.error(
              'TESTING_LOG - Missing backend data in confirmed mode:',
              error
            );
          }
          // Fall through to frontend calculation as recovery
        }
      }

      // Frontend calculation for preview mode or error recovery
      if (mode === 'preview' || mode === 'error') {
        return calculatePreviewTotals();
      }

      // If we reach here in confirmed mode, something went wrong
      if (mode === 'confirmed') {
        const error = new Error('Failed to calculate confirmed invoice totals');
        if (onError) {
          onError(error);
        } else {
          console.error(
            'TESTING_LOG - Failed to calculate confirmed totals:',
            error
          );
        }
        // Fall back to preview calculation as recovery
        return calculatePreviewTotals();
      }

      // Default fallback
      return calculatePreviewTotals();
    } catch (error) {
      console.error('TESTING_LOG - Error in calculateTotals:', error);
      if (onError) {
        onError(error);
      }
      // Return basic calculation as last resort
      return calculatePreviewTotals();
    }
  };

  const calculatePreviewTotals = () => {
    const servicesSubtotal = selectedServices.reduce((sum, service) => {
      const quantity = service.quantity || 1;
      const price = service.discounted_price || service.price || 0;
      return sum + price * quantity;
    }, 0);

    const queueJoiningFee =
      !excludeJoiningFee &&
      queueDetail?.price_type === 'Paid' &&
      queueDetail?.price
        ? queueDetail.price
        : 0;
    let servicesPreTaxTotal = 0;
    let servicesTotalTax = 0;

    selectedServices.forEach((service) => {
      const servicePrice = parseFloat(
        service.discounted_price || service.price || 0
      );
      const quantity = service.quantity || 1;
      const gstRate = parseFloat(service.gst_rate || 0);
      const totalServicePrice = servicePrice * quantity;

      if (gstRate > 0) {
        // For frontend calculation, apply tax to discounted price (correct approach)
        const taxAmount = totalServicePrice * (gstRate / 100);

        servicesPreTaxTotal += totalServicePrice;
        servicesTotalTax += taxAmount;
      } else {
        servicesPreTaxTotal += totalServicePrice;
      }
    });

    const subtotal = servicesPreTaxTotal + queueJoiningFee;
    const totalTax = servicesTotalTax;
    const total = subtotal + totalTax;

    return {
      servicesSubtotal,
      servicesPreTaxTotal,
      servicesTotalTax,
      queueJoiningFee,
      subtotal,
      totalTax,
      total,
      hasGST: servicesTotalTax > 0,
    };
  };

  const getPaymentStatusInfo = () => {
    const status = paymentStatus || 'pending';
    const statusLower = status.toLowerCase();

    let color, backgroundColor;

    if (
      statusLower === 'success' ||
      statusLower === 'paid' ||
      statusLower === 'completed'
    ) {
      color = '#4CAF50';
      backgroundColor = '#E8F5E8';
    } else if (statusLower === 'failed' || statusLower === 'error') {
      color = '#F44336';
      backgroundColor = '#FFEBEE';
    } else if (statusLower === 'processing') {
      color = '#FF9800';
      backgroundColor = '#FFF3E0';
    } else {
      color = '#2196F3';
      backgroundColor = '#E3F2FD';
    }

    return {
      text: `Payment ${status}`,
      color,
      backgroundColor,
    };
  };

  const {
    servicesSubtotal,
    servicesPreTaxTotal,
    servicesTotalTax,
    queueJoiningFee,
    subtotal,
    totalTax,
    total,
    hasGST,
  } = calculateTotals();
  const paymentStatusInfo = getPaymentStatusInfo();
  const currentDate = new Date().toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const handleBackPress = () => {
    if (onBack) {
      onBack();
    } else if (navigation) {
      goBackHandler(navigation);
    }
  };

  const handleNext = () => {
    onNext(selectedServices);
  };

  const handlePayNow = () => {
    if (onPayNow) {
      onPayNow(selectedServices);
    } else {
      onNext(selectedServices);
    }
  };

  const handleViewPaymentDetails = () => {
    const orderId = serviceOrderId || paymentId;
    // Only navigate if orderId exists and is a valid UUID (confirmed mode)
    if (orderId && nav && mode === 'confirmed') {
      nav.navigate('ServiceOrdersScreen', { orderId });
    }
  };

  const handleSettleOffline = () => {
    if (onSettleOffline && paymentId) {
      onSettleOffline(paymentId);
    }
  };

  const shouldShowOfflinePayment =
    onSettleOffline &&
    paymentStatus &&
    paymentStatus.toLowerCase() !== 'success' &&
    paymentStatus.toLowerCase() !== 'paid' &&
    paymentStatus.toLowerCase() !== 'completed';

  // Payment method handlers
  const handleSkipPayment = () => {
    if (onSkipPayment) {
      onSkipPayment();
    } else {
      onNext(selectedServices);
    }
  };

  const handleOnlinePayment = () => {
    if (onOnlinePayment) {
      onOnlinePayment();
    } else {
      handlePayNow();
    }
  };

  const handleCashPayment = () => {
    if (onCashPayment) {
      onCashPayment();
    } else {
      handleSettleOffline();
    }
  };

  return (
    <View style={styles.mainContainer}>
      {!showQueueInfo && !hideBackButton && (
        <View style={styles.headerContainer}>
          <TouchableOpacity
            onPress={handleBackPress}
            style={styles.backButton}
            accessible={true}
            accessibilityLabel="back_button"
          >
            <BackArrowIcon color={theme.colors.secondaryText} />
          </TouchableOpacity>
        </View>
      )}

      <View style={[styles.container, fullWidth && { maxWidth: '100%' }]}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            fullWidth && styles.fullWidthScrollContent,
          ]}
        >
          {/* <View style={styles.headerSection}>
            <QText style={styles.headerTitle}>
              {t('show_invoice_and_get_food')}
            </QText>
          </View> */}
          <View
            style={[
              styles.servicesCard,
              { marginTop: fullWidth ? 0 : 40 },
              fullWidth && styles.fullWidthCard,
            ]}
          >
            <View style={styles.servicesHeader}>
              <QText style={styles.servicesTitle}>Services</QText>
              {!hidePaymentStatus && (
                <View style={styles.paymentStatusSection}>
                  <View
                    style={[
                      styles.paymentStatusBadge,
                      {
                        backgroundColor: paymentStatusInfo.backgroundColor,
                        borderColor: paymentStatusInfo.color,
                      },
                    ]}
                  >
                    <QText
                      style={[
                        styles.paymentStatusText,
                        { color: paymentStatusInfo.color },
                      ]}
                    >
                      {paymentStatusInfo.text}
                    </QText>
                  </View>
                  <View style={styles.paymentActionsRow}>
                    {shouldShowOfflinePayment && (
                      <TouchableOpacity
                        style={styles.settleOfflineButton}
                        onPress={handleSettleOffline}
                        activeOpacity={0.7}
                      >
                        <QText style={styles.settleOfflineText}>
                          {t('settle_offline')}
                        </QText>
                      </TouchableOpacity>
                    )}

                    {/* Only show the button in confirmed mode with valid service order */}
                    {mode === 'confirmed' && serviceOrderId && (
                      <TouchableOpacity
                        style={styles.paymentDetailsLink}
                        onPress={handleViewPaymentDetails}
                      >
                        <QText style={styles.paymentDetailsLinkText}>
                          {serviceOrderId
                            ? t('view_service_order')
                            : t('view_payment_details')}
                        </QText>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              )}
            </View>

            {/* Services Details */}
            <View style={styles.servicesDetails}>
              {/* <View style={styles.detailRow}>
                <QText style={styles.detailLabel}>{t('invoice_number')}</QText>
                <QText style={styles.detailValue}>{invoiceNumber}</QText>
              </View> */}

              <View style={styles.detailRow}>
                <QText style={styles.detailLabel}>{t('purchase_date')}</QText>
                <QText style={styles.detailValue}>{currentDate}</QText>
              </View>
            </View>

            {/* Table Header */}
            <View style={styles.tableHeader}>
              <QText style={[styles.tableHeaderText, { flex: 3 }]}>
                {t('item')}
              </QText>
              <QText
                style={[
                  styles.tableHeaderText,
                  { flex: 1, textAlign: 'center' },
                ]}
              >
                {t('quantity')}
              </QText>
              <QText
                style={[
                  styles.tableHeaderText,
                  { flex: 1, textAlign: 'center' },
                ]}
              >
                {t('price')}
              </QText>
              <QText
                style={[
                  styles.tableHeaderText,
                  { flex: 1, textAlign: 'center' },
                ]}
              >
                {t('total')}
              </QText>
            </View>

            <View style={styles.itemsContainer}>
              {/* Queue Joining Fee */}
              {queueJoiningFee > 0 && (
                <View style={styles.itemRow}>
                  <QText style={[styles.itemText, { flex: 3 }]}>
                    {t('queue_joining_fee')}
                  </QText>
                  <QText
                    style={[styles.itemText, { flex: 1, textAlign: 'center' }]}
                  >
                    1
                  </QText>
                  <QText
                    style={[styles.itemText, { flex: 1, textAlign: 'center' }]}
                  >
                    {queueDetail?.currency_code || 'INR'} {queueJoiningFee}
                  </QText>
                  <QText
                    style={[styles.itemText, { flex: 1, textAlign: 'center' }]}
                  >
                    {queueDetail?.currency_code || 'INR'} {queueJoiningFee}
                  </QText>
                </View>
              )}

              {/* Selected Services */}
              {selectedServices.map((service, index) => {
                const quantity = service.quantity || 1;
                const unitPrice =
                  service.discounted_price || service.price || 0;
                const totalPrice = unitPrice * quantity;

                return (
                  <View key={service.id || index} style={styles.itemRow}>
                    <View style={[styles.itemText, { flex: 3 }]}>
                      <QText style={styles.serviceName}>{service.name}</QText>
                      {Boolean(service.gst_rate) &&
                        parseFloat(service.gst_rate) > 0 && (
                          <QText style={styles.serviceGstRate}>
                            GST: {service.gst_rate}%
                          </QText>
                        )}

                      {/* Display selected preferences */}
                      {service.preferences &&
                        Array.isArray(service.preferences) &&
                        service.preferences.length > 0 && (
                          <View style={styles.preferencesContainer}>
                            {service.preferences
                              .filter(
                                (preference) =>
                                  preference &&
                                  preference.name &&
                                  preference.value
                              )
                              .map((preference, prefIndex) => (
                                <QText
                                  key={prefIndex}
                                  style={styles.preferenceText}
                                >
                                  {String(preference.name)}:{' '}
                                  {String(preference.value)}
                                </QText>
                              ))}
                          </View>
                        )}

                      {/* Fallback for old preferences object format */}
                      {service.preferences &&
                        !Array.isArray(service.preferences) &&
                        Object.keys(service.preferences).length > 0 && (
                          <View style={styles.preferencesContainer}>
                            {Object.entries(service.preferences).map(
                              ([preferenceName, selectedOption], prefIndex) => (
                                <QText
                                  key={prefIndex}
                                  style={styles.preferenceText}
                                >
                                  {String(preferenceName)}:{' '}
                                  {String(selectedOption)}
                                </QText>
                              )
                            )}
                          </View>
                        )}
                    </View>
                    <QText
                      style={[
                        styles.itemText,
                        { flex: 1, textAlign: 'center' },
                      ]}
                    >
                      {quantity}
                    </QText>
                    <View
                      style={[
                        styles.itemText,
                        { flex: 1, alignItems: 'center' },
                      ]}
                    >
                      {service?.discount_percentage &&
                      service.discount_percentage > 0 ? (
                        <View style={styles.discountPriceContainer}>
                          <QText style={styles.originalPriceText}>
                            {formatPrice(
                              service.price || 0,
                              queueDetail?.currency_code || 'INR'
                            )}
                          </QText>
                          <QText style={styles.discountedPriceText}>
                            {formatPrice(
                              unitPrice,
                              queueDetail?.currency_code || 'INR'
                            )}
                          </QText>
                          <QText style={styles.discountBadge}>
                            (-{service.discount_percentage}%)
                          </QText>
                        </View>
                      ) : (
                        <QText style={styles.priceText}>
                          {formatPrice(
                            unitPrice,
                            queueDetail?.currency_code || 'INR'
                          )}
                        </QText>
                      )}
                    </View>
                    <QText
                      style={[
                        styles.itemText,
                        { flex: 1, textAlign: 'center' },
                      ]}
                    >
                      {formatPrice(
                        totalPrice,
                        queueDetail?.currency_code || 'INR'
                      )}
                    </QText>
                  </View>
                );
              })}
            </View>

            <View style={styles.totalSection}>
              {hasGST ? (
                <>
                  {/* Show original total if we have backend values with discounts */}
                  {mode === 'confirmed' &&
                    selectedServices.some((s) => s.discount_percentage > 0) && (
                      <View style={styles.totalRow}>
                        <QText style={styles.subtotalLabel}>
                          {t('Original Total') || 'Original Total'}
                        </QText>
                        <QText style={styles.subtotalValue}>
                          {queueDetail?.currency_code || 'INR'}{' '}
                          {selectedServices
                            .reduce((sum, service) => {
                              const quantity = service.quantity || 1;
                              const originalPrice =
                                service.original_price || service.price || 0;
                              return sum + originalPrice * quantity;
                            }, 0)
                            .toFixed(2)}
                        </QText>
                      </View>
                    )}

                  {/* Show discount if applicable */}
                  {selectedServices.some((s) => s.discount_percentage > 0) && (
                    <View style={styles.totalRow}>
                      <QText style={styles.subtotalLabel}>
                        {t('discount')}
                      </QText>
                      <QText
                        style={[styles.subtotalValue, { color: '#27ae60' }]}
                      >
                        -{queueDetail?.currency_code || 'INR'}{' '}
                        {selectedServices
                          .reduce((sum, service) => {
                            const quantity = service.quantity || 1;
                            const originalPrice =
                              service.original_price || service.price || 0;
                            const discountedPrice =
                              service.discounted_price || service.price || 0;
                            const discount =
                              (originalPrice - discountedPrice) * quantity;
                            return sum + discount;
                          }, 0)
                          .toFixed(2)}
                      </QText>
                    </View>
                  )}

                  <View style={styles.totalRow}>
                    <QText style={styles.subtotalLabel}>{t('subtotal')}</QText>
                    <QText style={styles.subtotalValue}>
                      {queueDetail?.currency_code || 'INR'}{' '}
                      {subtotal.toFixed(2)}
                    </QText>
                  </View>
                  <View style={styles.totalRow}>
                    <QText style={styles.subtotalLabel}>{t('tax')}</QText>
                    <QText style={styles.subtotalValue}>
                      {queueDetail?.currency_code || 'INR'}{' '}
                      {totalTax.toFixed(2)}
                    </QText>
                  </View>
                  <View style={[styles.totalRow, styles.finalTotalRow]}>
                    <QText style={styles.totalLabel}>{t('total')}</QText>
                    <QText style={styles.totalValue}>
                      {queueDetail?.currency_code || 'INR'} {total.toFixed(2)}
                    </QText>
                  </View>
                </>
              ) : (
                <>
                  {/* Show discount section even without GST if applicable */}
                  {selectedServices.some((s) => s.discount_percentage > 0) && (
                    <>
                      <View style={styles.totalRow}>
                        <QText style={styles.subtotalLabel}>
                          {t('Original Total') || 'Original Total'}
                        </QText>
                        <QText style={styles.subtotalValue}>
                          {queueDetail?.currency_code || 'INR'}{' '}
                          {selectedServices
                            .reduce((sum, service) => {
                              const quantity = service.quantity || 1;
                              const originalPrice =
                                service.original_price || service.price || 0;
                              return sum + originalPrice * quantity;
                            }, 0)
                            .toFixed(2)}
                        </QText>
                      </View>
                      <View style={styles.totalRow}>
                        <QText style={styles.subtotalLabel}>
                          {t('discount')}
                        </QText>
                        <QText
                          style={[styles.subtotalValue, { color: '#27ae60' }]}
                        >
                          -{queueDetail?.currency_code || 'INR'}{' '}
                          {selectedServices
                            .reduce((sum, service) => {
                              const quantity = service.quantity || 1;
                              const originalPrice =
                                service.original_price || service.price || 0;
                              const discountedPrice =
                                service.discounted_price || service.price || 0;
                              const discount =
                                (originalPrice - discountedPrice) * quantity;
                              return sum + discount;
                            }, 0)
                            .toFixed(2)}
                        </QText>
                      </View>
                    </>
                  )}
                  <View style={styles.totalRow}>
                    <QText style={styles.totalLabel}>{t('total')}</QText>
                    <QText style={styles.totalValue}>
                      {queueDetail?.currency_code || 'INR'} {total.toFixed(2)}
                    </QText>
                  </View>
                </>
              )}
            </View>
          </View>
        </ScrollView>
      </View>

      {showFooterButtons && (
        <View style={styles.footerContainer}>
          {showPaymentOptions ? (
            <View style={styles.buttonColumn}>
              {isUserFlow ? (
                // For users: Show only online payment button
                <Button
                  mode="contained"
                  onPress={handleOnlinePayment}
                  style={[styles.fullWidthButton, styles.makePaymentButton]}
                >
                  <QText style={styles.makePaymentButtonText}>
                    {t('pay_now')}
                  </QText>
                </Button>
              ) : (
                // For operators: Show online and offline payment buttons directly
                <>
                  <Button
                    mode="contained"
                    onPress={handleOnlinePayment}
                    style={[styles.fullWidthButton, styles.onlinePaymentButton]}
                  >
                    <QText style={styles.onlinePaymentButtonText}>
                      {t('online_payment_recommended')}
                    </QText>
                  </Button>
                  <Button
                    mode="outlined"
                    onPress={handleCashPayment}
                    style={[
                      styles.fullWidthButton,
                      styles.offlinePaymentButton,
                    ]}
                  >
                    <QText style={styles.offlinePaymentButtonText}>
                      {t('cash_payment')}
                    </QText>
                  </Button>
                </>
              )}
            </View>
          ) : (
            // Original buttons for backward compatibility
            <View style={styles.buttonRow}>
              <Button
                mode="contained"
                onPress={
                  onBack || (() => navigation && goBackHandler(navigation))
                }
                style={[styles.cancelButton, styles.sideButton]}
              >
                <QText
                  style={{
                    color: theme.colors.blackTxt,
                    fontSize: 16,
                    fontWeight: theme.fontWeight.button,
                  }}
                >
                  {t('back')}
                </QText>
              </Button>

              <Button
                mode="contained"
                onPress={handlePayNow}
                style={[styles.sideButton, styles.payButton]}
                disabled={
                  paymentStatus === 'paid' || paymentStatus === 'completed'
                }
              >
                <QText
                  style={{
                    color: theme.colors.whiteTxt,
                    fontSize: 16,
                    fontWeight: theme.fontWeight.button,
                  }}
                >
                  {paymentStatus === 'paid' || paymentStatus === 'completed'
                    ? t('paid')
                    : isAddUserFlow
                      ? t('submit')
                      : t('pay_now')}
                </QText>
              </Button>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    mainContainer: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    headerContainer: {
      paddingHorizontal: 15,
      paddingVertical: 10,
      paddingTop: 20,
    },
    backButton: {
      width: 40,
      height: 40,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 20,
      backgroundColor: theme.colors.onBackground,
    },
    container: {
      flex: 1,
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
    },
    scrollContent: {
      padding: 20,
      flexGrow: 1,
    },
    headerSection: {
      marginBottom: 30,
      paddingHorizontal: 20,
      paddingVertical: 15,
      backgroundColor: theme.colors.onBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.borderColor,
    },
    headerTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      textAlign: 'center',
    },
    servicesCard: {
      backgroundColor: theme.colors.onBackground,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    servicesHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 30,
      paddingBottom: 15,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderColor,
    },
    servicesTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      letterSpacing: 1,
    },
    servicesDetails: {
      marginBottom: 30,
    },
    detailRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    detailLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.secondaryText,
    },
    detailValue: {
      fontSize: 14,
      color: theme.colors.text,
    },
    tableHeader: {
      flexDirection: 'row',
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderColor,
      marginBottom: 10,
      backgroundColor: theme.colors.background,
      borderRadius: 6,
      paddingHorizontal: 8,
    },
    tableHeaderText: {
      fontSize: 12,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      textTransform: 'uppercase',
    },
    itemsContainer: {
      marginBottom: 40,
    },
    itemRow: {
      flexDirection: 'row',
      paddingVertical: 12,
      paddingHorizontal: 8,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.borderColor,
    },
    itemText: {
      fontSize: 14,
      color: theme.colors.text,
    },
    totalSection: {
      paddingVertical: 15,
      paddingHorizontal: 8,
      borderTopWidth: 2,
      borderTopColor: theme.colors.borderColor,
      marginTop: 10,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      letterSpacing: 1,
    },
    totalValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      letterSpacing: 1,
    },
    footerContainer: {
      paddingHorizontal: 15,
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
      marginBottom: 15,
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 10,
    },
    cancelButton: {
      backgroundColor: theme.colors.white,
    },
    sideButton: {
      flex: 1,
    },
    payButton: {
      backgroundColor: theme.colors.primary,
    },
    fullWidthCard: {
      width: '100%',
    },
    fullWidthScrollContent: {
      padding: 0,
    },
    paymentStatusBadge: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderWidth: 1,
      borderRadius: 12,
      minWidth: 80,
      alignItems: 'center',
    },
    paymentStatusText: {
      fontSize: 11,
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    paymentStatusSection: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 8,
    },
    paymentActionsRow: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 8,
      width: '100%',
    },
    paymentDetailsLink: {
      padding: 4,
    },
    paymentDetailsLinkText: {
      fontSize: 12,
      fontWeight: '500',
      color: theme.colors.primary,
      textDecorationLine: 'underline',
    },
    settleOfflineButton: {
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      borderRadius: 6,
      backgroundColor: theme.colors.onBackground,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
      width: '100%',
      minHeight: 40,
    },
    settleOfflineText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.primary,
      textAlign: 'center',
    },

    serviceName: {
      fontSize: 14,
      color: theme.colors.text,
      fontWeight: '500',
    },
    serviceGstRate: {
      fontSize: 11,
      color: theme.colors.text,
      marginTop: 2,
      fontWeight: '500',
      opacity: 0.8,
    },
    subtotalLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.secondaryText,
    },
    subtotalValue: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.secondaryText,
    },
    finalTotalRow: {
      borderTopWidth: 2,
      borderTopColor: theme.colors.primary,
      paddingTop: 12,
      marginTop: 8,
    },
    // New payment method styles
    buttonColumn: {
      flexDirection: 'column',
      gap: 12,
    },
    fullWidthButton: {
      width: '100%',
      paddingVertical: 12,
    },
    skipButton: {
      borderColor: theme.colors.outline,
      backgroundColor: theme.colors.surface,
    },
    skipButtonText: {
      color: theme.colors.onSurface,
      fontSize: 16,
      fontWeight: '500',
    },
    makePaymentButton: {
      backgroundColor: theme.colors.primary,
    },
    makePaymentButtonText: {
      color: theme.colors.onPrimary,
      fontSize: 16,
      fontWeight: '600',
    },
    onlinePaymentButton: {
      backgroundColor: theme.colors.primary,
    },
    onlinePaymentButtonText: {
      color: theme.colors.onPrimary,
      fontSize: 16,
      fontWeight: '600',
    },
    offlinePaymentButton: {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.onBackground,
    },
    offlinePaymentButtonText: {
      color: theme.colors.primary,
      fontSize: 16,
      fontWeight: '600',
    },
    preferencesContainer: {
      marginTop: 4,
      paddingLeft: 10,
    },
    preferenceText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.8,
    },
    discountPriceContainer: {
      alignItems: 'center',
    },
    originalPriceText: {
      fontSize: 11,
      color: theme.colors.placeHolderText,
      textDecorationLine: 'line-through',
      marginBottom: 2,
    },
    discountedPriceText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.primary,
    },
    discountBadge: {
      fontSize: 10,
      color: theme.colors.success,
      fontWeight: '600',
      backgroundColor: theme.colors.success + '15',
      paddingHorizontal: 4,
      paddingVertical: 1,
      borderRadius: 4,
      marginTop: 2,
    },
    priceText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.text,
    },
  });

export default InvoiceComponent;
